import { Pressable } from 'react-native';
import { Text, XStack } from 'tamagui';
import { useThemeColors, Icon, type IconName } from '@kiips/ui';

interface PickedPlaceRowProps {
  /** Leading glyph. Defaults to the map pin used by every picker. */
  icon?: IconName | undefined;
  name: string;
  /** Trailing action text — "Clear" for a pick the user can undo,
   *  "Change" for a value we derived on their behalf. */
  actionLabel: string;
  onAction: () => void;
  /** Emphasise the action (used when it's an offer rather than an
   *  undo, e.g. changing a derived journey origin). */
  emphasised?: boolean | undefined;
}

/**
 * The "here's what you picked, tap to undo" line that sits under a
 * `PlaceLookupRow`. Extracted because the same row is rendered at four
 * call sites — trip creation, and the stay / journey / derived-origin
 * slots of the add sheet — which had already drifted into two variants.
 */
export function PickedPlaceRow({
  icon = 'place',
  name,
  actionLabel,
  onAction,
  emphasised,
}: PickedPlaceRowProps) {
  const theme = useThemeColors();
  return (
    <XStack alignItems="center" gap={8} paddingHorizontal={6}>
      <Icon name={icon} size={14} color={theme.textSubdued} />
      <Text fontSize={13} color="$textOnSurface" flex={1} numberOfLines={1}>{name}</Text>
      <Pressable onPress={onAction} accessibilityLabel={`${actionLabel} ${name}`} hitSlop={8}>
        <Text
          fontSize={12}
          fontWeight={emphasised ? '700' : '400'}
          color={emphasised ? '$accentCerulean' : '$textSubdued'}
        >
          {actionLabel}
        </Text>
      </Pressable>
    </XStack>
  );
}
